import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, MenuItem, Paper, Switch, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CostingService } from '../../services/costing.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { Account } from '../../interfaces/accounting.interface';
import { CostingSettings } from '../../interfaces/costing.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';

const DRIVERS = [
  { value: 'PLANNED_CM_VALUE', label: 'Sewn quantity x planned CM per garment' },
  { value: 'OUTPUT_QTY', label: 'Sewn quantity' },
];

/** The accounts order costing cannot be enabled without, and the absorbed account that absorption needs. */
export const missingCostingRoles = (s: Pick<CostingSettings, 'wipAccountId' | 'fgAccountId' | 'cogsAccountId' | 'absorbedAccountId'>): string[] => {
  const out: string[] = [];
  if (!s.wipAccountId) out.push('WIP account');
  if (!s.fgAccountId) out.push('Finished goods account');
  if (!s.cogsAccountId) out.push('COGS account');
  if (!s.absorbedAccountId) out.push('Absorbed overhead account');
  return out;
};

const CostingSettingsPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.COSTING_MANAGE);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [s, setS] = useState<CostingSettings | null>(null);
  /** What the server holds; the banner follows this, not the unsaved toggle. */
  const [savedEnabled, setSavedEnabled] = useState(false);

  const load = useCallback(async () => {
    try {
      const [settings, accs] = await Promise.all([CostingService.getSettings(institutionId), AccountService.getAllAccounts()]);
      setS(settings); setSavedEnabled(settings.enabled); setAccounts(accs);
    } catch (err) { toast.showApiError(err, 'Could not load costing settings'); }
  }, [institutionId, toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!s) return;
    try {
      const { institutionId: _ignored, ...req } = s;
      const saved = await CostingService.saveSettings(institutionId, req);
      setS(saved); setSavedEnabled(saved.enabled);
      toast.showSuccess('Settings saved');
    } catch (err) { toast.showApiError(err, 'Could not save settings'); }
  };

  const suggest = async () => {
    try {
      const r = await CostingService.createSuggestedAccounts(institutionId);
      setS(r.settings);
      setSavedEnabled(r.settings.enabled);
      setAccounts(await AccountService.getAllAccounts());
      const missing = r.missingRoles ?? [];
      if (missing.length > 0 || r.skipped.length > 0) {
        toast.showWarning(`Accounts created: ${r.created.length}, already present: ${r.present.length}. `
          + (missing.length > 0 ? `Still to pick manually: ${missing.join(', ')}. ` : '') + r.skipped.join('; '));
      } else if (r.created.length === 0) {
        toast.showSuccess('All roles already have accounts. Nothing was created.');
      } else {
        toast.showSuccess(`Accounts created: ${r.created.length}, already present: ${r.present.length}`);
      }
    } catch (err) { toast.showApiError(err, 'Could not create the suggested accounts'); }
  };

  if (!s) return <Box sx={{ p: 2 }}><Typography>Loading...</Typography></Box>;
  const missingRoles = missingCostingRoles(s);
  const pick = (label: string, key: 'wipAccountId' | 'fgAccountId' | 'cogsAccountId' | 'absorbedAccountId' | 'writeOffAccountId', help: Record<string, string>) => (
    <Box sx={{ flex: '1 1 280px', maxWidth: 380 }} {...help}>
      <SearchableSelect<Account> options={accounts} value={s[key] ?? ''} onChange={id => setS({ ...s, [key]: id || null })}
        getOptionLabel={a => `${a.code} - ${a.name}`} label={label} disabled={!canManage} />
    </Box>
  );

  return (
    <Box sx={{ p: 2 }} data-help-page="costing.settings.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Costing settings</Typography>
        <Button data-help="costing.settings.orders" component={RouterLink} to="/costing/orders">Order costs</Button>
      </Box>
      {savedEnabled
        ? <Alert severity="success" sx={{ mb: 2 }} data-testid="costing-state">Order costing is on.</Alert>
        : <Alert severity="info" sx={{ mb: 2 }} data-testid="costing-state">Costing is off until you switch it on here. Turning it on needs the WIP, finished goods and COGS accounts.</Alert>}
      {missingRoles.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="costing-missing-roles">
          Still needed before costing can be switched on: {missingRoles.join(', ')}. Pick them below or use Create suggested accounts.
        </Alert>
      )}
      <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="costing.settings.form">
        <FormControlLabel data-help="costing.settings.field.enabled" control={<Switch checked={s.enabled} disabled={!canManage} onChange={e => setS({ ...s, enabled: e.target.checked })} />} label="Order costing enabled" sx={{ flex: '1 1 100%' }} />
        {pick('WIP account', 'wipAccountId', { 'data-help': 'costing.settings.field.wip' })}
        {pick('Finished goods account', 'fgAccountId', { 'data-help': 'costing.settings.field.fg' })}
        {pick('COGS account', 'cogsAccountId', { 'data-help': 'costing.settings.field.cogs' })}
        {pick('Absorbed overhead account', 'absorbedAccountId', { 'data-help': 'costing.settings.field.absorbed' })}
        {pick('Write-off account (cancelled orders, optional)', 'writeOffAccountId', { 'data-help': 'costing.settings.field.writeOff' })}
        <TextField data-help="costing.settings.field.driver" sx={{ flex: '1 1 200px', maxWidth: 260 }} size="small" select label="Absorption driver" value={s.absorptionDriver} disabled={!canManage}
          onChange={e => setS({ ...s, absorptionDriver: e.target.value as CostingSettings['absorptionDriver'] })}>
          {DRIVERS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
        </TextField>
        <TextField data-help="costing.settings.field.tolerance" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="number" label="Over-output tolerance %" value={s.overshipTolerancePct} disabled={!canManage}
          onChange={e => setS({ ...s, overshipTolerancePct: Number(e.target.value) })} />
        <Box sx={{ flex: '1 1 100%' }} data-help-section="costing.settings.pool">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Overhead pool accounts</Typography>
          <Autopool accounts={accounts} value={s.poolAccountIds} disabled={!canManage} onChange={ids => setS({ ...s, poolAccountIds: ids })} />
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: '1 1 100%' }}>
            <Button data-help="costing.settings.save" variant="contained" onClick={save}>Save</Button>
            <Button data-help="costing.settings.suggest" variant="outlined" onClick={suggest}>Create suggested accounts</Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/** Multi-select of pool accounts built from the single-select: chosen accounts show as removable rows. */
const Autopool: React.FC<{ accounts: Account[]; value: string[]; disabled?: boolean; onChange: (ids: string[]) => void }> = ({ accounts, value, disabled, onChange }) => {
  const [pending, setPending] = useState('');
  const byId = new Map(accounts.map(a => [a.id, a]));
  return (
    <Box>
      {value.map(id => (
        <Box key={id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" sx={{ flex: '1 1 auto' }}>{byId.get(id) ? `${byId.get(id)!.code} - ${byId.get(id)!.name}` : id}</Typography>
          {!disabled && <Button data-help="costing.settings.pool.remove" size="small" color="error" onClick={() => onChange(value.filter(v => v !== id))}>Remove</Button>}
        </Box>
      ))}
      {!disabled && (
        <Box sx={{ flex: '1 1 280px', maxWidth: 380 }} data-help="costing.settings.pool.add">
          <SearchableSelect<Account> options={accounts.filter(a => a.id && !value.includes(a.id))} value={pending}
            onChange={id => { if (id) { onChange([...value, id]); } setPending(''); }}
            getOptionLabel={a => `${a.code} - ${a.name}`} label="Add pool account" />
        </Box>
      )}
    </Box>
  );
};

export default CostingSettingsPage;
